<!--
  `dlg-self-layout` opts this dialog out of the body gutter that
  `src/dialogs.css` puts on every direct child of a dialog root. It does
  NOT opt out of the shared look: the header band and footer bar below
  read the same `--dlg-*` tokens as every other dialog, so this stays
  consistent with them. The opt-out exists because this body is a
  full-height flex column with a sidebar that has to reach the card
  edge, which a 24px gutter would break.
-->
<div class="change-packages-container dlg-self-layout">

  <!-- ═══ HEADER ═══ -->
  <div class="cp-header">
    <h2 class="cp-title">
      <mat-icon class="cp-title-icon">inventory_2</mat-icon>
      <span i18n="@@Change Packages">Change Packages</span>
      <span class="cp-order-id">#{{printNodeOrder.ID}}</span>
    </h2>
    <div class="cp-header-actions">
      <button mat-stroked-button (click)="addPackage()" class="cp-add-pkg-btn">
        <mat-icon>add</mat-icon> <span i18n="@@Add Package">Add Package</span>
      </button>
      <button mat-stroked-button (click)="getAllShippingRates()" class="cp-rates-btn">
        <mat-icon>request_quote</mat-icon> <span i18n="@@Get All Rates">Get All Rates</span>
      </button>
    </div>
  </div>

  <!-- ═══ DESTINATION ═══ -->
  <div class="cp-destination">
    <mat-icon class="cp-dest-icon">place</mat-icon>
    <span>{{printNodeOrder.address1}}<span *ngIf="printNodeOrder.address2">, {{printNodeOrder.address2}}</span>,
      {{printNodeOrder.city}}, {{printNodeOrder.state}} {{printNodeOrder.postalcode}}, {{printNodeOrder.country}}</span>
  </div>

  <!-- ═══ QUANTITY WARNINGS ═══ -->
  <div *ngIf="hasQuantityMismatch" class="cp-warning-bar">
    <mat-icon>warning</mat-icon>
    <span i18n="@@Item quantities do not match package assignments">Item quantities do not match package assignments</span>
  </div>

  <!-- ═══ STAGE 1: EDIT ═══ -->
  <ng-container *ngIf="stage === 'edit'">

    <!-- ── ITEMS SIDEBAR ── -->
    <div class="cp-layout">
      <div class="cp-items-panel" [class.cp-items-collapsed]="!itemsPanelExpanded && mobileQueryMatches">
        <h3 class="cp-section-title cp-items-toggle" (click)="itemsPanelExpanded = !itemsPanelExpanded">
          <span i18n="@@Order Items">Order Items</span>
          <span class="cp-item-count-badge">{{allItems.length}}</span>
          <mat-icon class="cp-toggle-icon">{{itemsPanelExpanded || !mobileQueryMatches ? 'expand_less' : 'expand_more'}}</mat-icon>
        </h3>
        <div class="cp-items-list" [class.cp-items-hidden]="!itemsPanelExpanded && mobileQueryMatches">
        <div *ngFor="let item of allItems" class="cp-item-card" [class.cp-item-mismatch]="getQuantityUnassigned(item) !== 0">
          <div class="cp-item-header">
            <span class="cp-item-title">{{item.book?.title}}</span>
            <span class="cp-item-id">#{{item.book_ID}}</span>
          </div>
          <div class="cp-item-details">
            <span class="cp-item-qty">
              <mat-icon class="cp-inline-icon">layers</mat-icon>
              {{item.quantity}} <span i18n="@@copies">copies</span>
            </span>
            <span class="cp-item-trim">
              {{item.book.body.width | measurePointsValue:1}}×{{item.book.body.height | measurePoints:1}}
            </span>
          </div>
          <div class="cp-item-details">
            <span class="cp-item-weight">
              <mat-icon class="cp-inline-icon">scale</mat-icon>
              {{item.book.weight | measureWeight:2}} <span i18n="each|Abbreviation for &quot;each&quot;, shown after a per-copy weight, e.g. &quot;0.55 lbs ea&quot;. Not the pronoun.@@ea">ea</span>
            </span>
            <span class="cp-item-spine">
              <mat-icon class="cp-inline-icon">straighten</mat-icon>
              {{getBookSpineInches(item) | measureLength:2}} <span i18n="book spine|The bound edge of a book. Labels the thickness measurement that precedes it, e.g. &quot;0.75 in spine&quot;.@@spine">spine</span>
            </span>
          </div>
          <div class="cp-item-details" *ngIf="item.book.bindingType === 'COIL'">
            <span class="cp-item-binding-note">
              <mat-icon class="cp-inline-icon">auto_fix_high</mat-icon>
              <span i18n="@@COIL effective width">COIL — eff. width</span> {{getBookWidthInches(item) | measureLength:2}}
            </span>
          </div>
          <div *ngIf="getQuantityUnassigned(item) !== 0" class="cp-item-unassigned"
               [class.cp-item-over]="getQuantityUnassigned(item) < 0"
               [class.cp-item-under]="getQuantityUnassigned(item) > 0">
            <mat-icon>{{getQuantityUnassigned(item) > 0 ? 'remove_circle_outline' : 'error'}}</mat-icon>
            <span *ngIf="getQuantityUnassigned(item) > 0">{{getQuantityUnassigned(item)}} <span i18n="copies not yet placed in any package|Follows a count, e.g. &quot;3 unassigned&quot;.@@unassigned">unassigned</span></span>
            <span *ngIf="getQuantityUnassigned(item) < 0">{{-getQuantityUnassigned(item)}} <span i18n="more copies placed in packages than the order contains|Follows a count, e.g. &quot;2 over-assigned&quot;.@@over-assigned">over-assigned</span></span>
          </div>
        </div>
        </div>
      </div>

      <!-- ── PACKAGES AREA ── -->
      <div class="cp-packages-panel">
        <div class="cp-packages-scroll">
          <div *ngFor="let pkg of packagesArray" class="cp-package-card"
               [class.cp-pkg-shipped]="pkg.shippingStatus === 'SHIPPED'"
               [class.cp-pkg-labeled]="getPackageProtectionLevel(pkg) === 'label'"
               [class.cp-pkg-new]="pkg.package_ID < 0">

            <!-- Package Header -->
            <div class="cp-pkg-header">
              <div class="cp-pkg-title-row">
                <span class="cp-pkg-id">
                  <mat-icon *ngIf="pkg.package_ID < 0" class="cp-new-badge">fiber_new</mat-icon>
                  <span *ngIf="pkg.package_ID >= 0">#{{pkg.package_ID}}</span>
                  <span *ngIf="pkg.package_ID < 0" i18n="@@New">New</span>
                </span>
                <mat-icon *ngIf="getProtectionIcon(pkg)" class="cp-protection-icon"
                          [matTooltip]="getProtectionTooltip(pkg)">{{getProtectionIcon(pkg)}}</mat-icon>
                <span class="cp-pkg-status" [class.cp-status-shipped]="pkg.shippingStatus === 'SHIPPED'"
                      [class.cp-status-not-shipped]="pkg.shippingStatus !== 'SHIPPED'">
                  {{pkg.shippingStatus === 'SHIPPED' ? 'SHIPPED' : 'NOT SHIPPED'}}
                </span>
              </div>
              <div class="cp-pkg-tracking" *ngIf="pkg.trackingnumber">{{pkg.trackingnumber}}</div>
            </div>

            <!-- Carrier & Package Type Selectors -->
            <div class="cp-pkg-selectors">
              <mat-form-field class="cp-pkg-select">
                <mat-label i18n="@@Service Level">Service Level</mat-label>
                <mat-select [ngModel]="pkg.carrier + '_' + pkg.servicelevel"
                            (selectionChange)="changePackageCarrierAndServiceLevelCombined(pkg, $event.value)">
                  <mat-option *ngFor="let csl of carrierServiceLevels" [value]="csl.ID">
                    {{csl.carrier}} {{csl.serviceLevel}}
                  </mat-option>
                </mat-select>
              </mat-form-field>
              <mat-form-field class="cp-pkg-select">
                <mat-label i18n="@@Package Type">Package Type</mat-label>
                <mat-select [ngModel]="pkg.packageType_ID"
                            (selectionChange)="changePackageType(pkg, $event.value)">
                  <mat-option *ngFor="let pt of getFilteredPackageTypes(pkg)" [value]="pt.ID">
                    {{pt.name}}
                  </mat-option>
                </mat-select>
              </mat-form-field>
            </div>

            <!-- Capacity Gauges -->
            <div class="cp-pkg-gauges">
              <div class="cp-gauge">
                <div class="cp-gauge-label">
                  <mat-icon class="cp-inline-icon">scale</mat-icon>
                  <span>{{getMetrics(pkg).totalWeight | measureWeight:1}}</span>
                  <span class="cp-gauge-max" *ngIf="getPackageTypeForPkg(pkg)"> / {{getPackageTypeForPkg(pkg).maxWeight | measureWeight:1}}</span>
                </div>
                <div class="cp-gauge-bar">
                  <div class="cp-gauge-fill" [class]="getCapacityBarClass(getMetrics(pkg).weightPercent)"
                       [style.width.%]="getMetrics(pkg).weightPercent > 100 ? 100 : getMetrics(pkg).weightPercent"></div>
                </div>
              </div>
              <div class="cp-gauge">
                <div class="cp-gauge-label">
                  <mat-icon class="cp-inline-icon">view_in_ar</mat-icon>
                  <span>{{getMetrics(pkg).totalBookVolume | measureVolume:0}}</span>
                  <span class="cp-gauge-max" *ngIf="getMetrics(pkg).packageVolume > 0"> / {{getMetrics(pkg).packageVolume | measureVolume:0}}</span>
                </div>
                <div class="cp-gauge-bar">
                  <div class="cp-gauge-fill" [class]="getCapacityBarClass(getMetrics(pkg).volumePercent)"
                       [style.width.%]="getMetrics(pkg).volumePercent > 100 ? 100 : getMetrics(pkg).volumePercent"></div>
                </div>
              </div>
              <div class="cp-gauge">
                <div class="cp-gauge-label">
                  <mat-icon class="cp-inline-icon">straighten</mat-icon>
                  <span i18n="@@Stacked Height">Stacked Height</span>: {{getMetrics(pkg).stackedHeight | measureLength:1}}
                </div>
              </div>
            </div>

            <!-- Thickness by Trim Size -->
            <div class="cp-pkg-trim-breakdown" *ngIf="getThicknessTrimSizes(pkg).length > 1">
              <div *ngFor="let ts of getThicknessTrimSizes(pkg)" class="cp-trim-row">
                <span class="cp-trim-label">{{ts}}</span>
                <span class="cp-trim-value">{{getMetrics(pkg).thicknessByTrimSize[ts] | measureLength:1}}</span>
              </div>
            </div>

            <!-- Items in this Package -->
            <div class="cp-pkg-items">
              <div *ngFor="let item of allItems" class="cp-pkg-item-row"
                   [class.cp-pkg-item-active]="getQuantityInPackage(item, pkg) > 0">
                <div class="cp-pkg-item-info">
                  <span class="cp-pkg-item-title">{{item.book?.title}}</span>
                </div>
                <div class="cp-pkg-item-controls">
                  <button mat-icon-button class="cp-qty-btn" (click)="changeQuantity(item, pkg, -1)"
                          [disabled]="getQuantityInPackage(item, pkg) <= 0"
                          matTooltip="Remove 1" i18n-matTooltip="@@Remove 1">
                    <mat-icon>remove</mat-icon>
                  </button>
                  <input type="number" class="cp-pkg-item-qty-input"
                         [class.cp-qty-zero]="getQuantityInPackage(item, pkg) === 0"
                         [class.cp-qty-over]="getQuantityUnassigned(item) < 0 && getQuantityInPackage(item, pkg) > 0"
                         [value]="getQuantityInPackage(item, pkg)"
                         min="0"
                         (change)="setQuantityInPackage(item, pkg, $any($event.target).value)">
                  <button mat-icon-button class="cp-qty-btn" (click)="changeQuantity(item, pkg, 1)"
                          [disabled]="getQuantityUnassigned(item) <= 0 && getQuantityInPackage(item, pkg) >= item.quantity"
                          matTooltip="Add 1" i18n-matTooltip="@@Add 1">
                    <mat-icon>add</mat-icon>
                  </button>

                  <!-- Transfer 1 to each other package -->
                  <ng-container *ngFor="let targetPkg of getTransferTargetPackages(pkg)">
                    <button mat-icon-button class="cp-transfer-btn" (click)="transferOneToPackage(item, pkg, targetPkg)"
                            [disabled]="getQuantityInPackage(item, pkg) <= 0"
                            [matTooltip]="'Send 1 → #' + targetPkg.package_ID">
                      <span class="cp-transfer-label">{{targetPkg.package_ID >= 0 ? '#' + targetPkg.package_ID : 'New'}}</span>
                    </button>
                  </ng-container>

                  <!-- Move all into this package -->
                  <button mat-icon-button class="cp-move-all-btn" (click)="moveAllToPackage(item, pkg)"
                          [disabled]="getQuantityInPackage(item, pkg) === item.quantity"
                          matTooltip="Move all into this package" i18n-matTooltip="@@Move all into this package">
                    <mat-icon>keyboard_double_arrow_left</mat-icon>
                  </button>
                </div>
                <div class="cp-pkg-item-weight" *ngIf="getQuantityInPackage(item, pkg) > 0">
                  {{getQuantityInPackage(item, pkg) * item.book.weight | measureWeight:2}} ·
                  {{getQuantityInPackage(item, pkg) * getBookSpineInches(item) | measureLength:1}}
                </div>
              </div>
            </div>

            <!-- Shipping Rates -->
            <div class="cp-pkg-rates" *ngIf="getRates(pkg).length > 0">
              <h4 class="cp-rates-title" i18n="@@Shipping Rates">Shipping Rates</h4>
              <div *ngFor="let rate of getRates(pkg)" class="cp-rate-row"
                   [class.cp-rate-selected]="rate.carrier === pkg.carrier && rate.serviceLevel === pkg.servicelevel">
                <span class="cp-rate-carrier">{{rate.carrier}} {{rate.serviceLevel}}</span>
                <span class="cp-rate-cost">${{rate.cost | number:'1.2-2'}}</span>
                <span class="cp-rate-days" *ngIf="rate.deliveryDays">{{rate.deliveryDays}} <span i18n="@@days">days</span></span>
              </div>
            </div>
            <div *ngIf="isRatesLoading(pkg)" class="cp-rates-loading">
              <mat-spinner diameter="20"></mat-spinner> <span i18n="@@Loading rates">Loading rates...</span>
            </div>

            <!-- Package Actions -->
            <div class="cp-pkg-actions">
              <button mat-stroked-button (click)="getShippingRates(pkg)" class="cp-rate-btn">
                <mat-icon>request_quote</mat-icon> <span i18n="@@Get Rates">Get Rates</span>
              </button>
              <button mat-stroked-button color="warn" (click)="deletePackage(pkg)"
                      [disabled]="!canDeletePackage(pkg)"
                      class="cp-delete-btn">
                <mat-icon>delete</mat-icon> <span i18n="destructive removal|Button that permanently destroys the record. Must be clearly different from Cancel, which only closes a dialog.@@Delete">Delete</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ── PENDING CHANGES SUMMARY ── -->
    <div *ngIf="hasPendingChanges" class="cp-changes-summary">
      <h3 class="cp-section-title">
        <mat-icon>pending_actions</mat-icon>
        <span i18n="@@Pending Changes">Pending Changes</span>
        <span class="cp-changes-count">{{pendingChanges.length}}</span>
      </h3>
      <div class="cp-changes-list">
        <div *ngFor="let change of pendingChanges" class="cp-change-item"
             [class.cp-change-move]="change.type === 'move'"
             [class.cp-change-add]="change.type === 'add_package'"
             [class.cp-change-delete]="change.type === 'delete_package'"
             [class.cp-change-refund]="change.type === 'usps_refund'">
          <mat-icon *ngIf="change.type === 'move'">swap_horiz</mat-icon>
          <mat-icon *ngIf="change.type === 'add_package'">add_box</mat-icon>
          <mat-icon *ngIf="change.type === 'delete_package'">delete</mat-icon>
          <mat-icon *ngIf="change.type === 'change_carrier'">local_shipping</mat-icon>
          <mat-icon *ngIf="change.type === 'change_package_type'">inventory_2</mat-icon>
          <mat-icon *ngIf="change.type === 'usps_refund'">money_off</mat-icon>
          <span>{{change.description}}</span>
        </div>
      </div>
    </div>

    <!-- ── FOOTER ACTIONS ── -->
    <div class="cp-footer">
      <button mat-stroked-button (click)="cancel()" i18n="dialog dismissal|Button that closes a dialog and discards any unsaved edits. It never removes or destroys existing data - the Delete button does that. Must translate to a word meaning 'abandon this action', not 'erase'.@@Cancel">Cancel</button>
      <button mat-raised-button color="primary" (click)="goToReview()"
              [disabled]="!hasPendingChanges || hasQuantityMismatch"
              i18n="@@ReviewCommit">Review & Commit</button>
    </div>
  </ng-container>

  <!-- ═══ STAGE 2: REVIEW ═══ -->
  <ng-container *ngIf="stage === 'review'">
    <div class="cp-review">
      <h3 class="cp-section-title">
        <mat-icon>fact_check</mat-icon>
        <span i18n="@@Review Changes">Review Changes</span>
      </h3>

      <div class="cp-review-list">
        <div *ngFor="let change of pendingChanges" class="cp-review-item"
             [class.cp-change-refund]="change.type === 'usps_refund'">
          <mat-icon *ngIf="change.type === 'move'">swap_horiz</mat-icon>
          <mat-icon *ngIf="change.type === 'add_package'">add_box</mat-icon>
          <mat-icon *ngIf="change.type === 'delete_package'">delete</mat-icon>
          <mat-icon *ngIf="change.type === 'change_carrier'">local_shipping</mat-icon>
          <mat-icon *ngIf="change.type === 'change_package_type'">inventory_2</mat-icon>
          <mat-icon *ngIf="change.type === 'usps_refund'">money_off</mat-icon>
          <span>{{change.description}}</span>
        </div>
      </div>

      <div *ngIf="hasQuantityMismatch" class="cp-warning-bar">
        <mat-icon>warning</mat-icon>
        <span i18n="@@Quantity mismatch warning">Some items have mismatched quantities. Please go back and correct.</span>
      </div>

      <div *ngIf="saveError" class="cp-warning-bar" style="background-color: #fdecea; color: #b71c1c;">
        <mat-icon>error_outline</mat-icon>
        <span>{{saveError}}</span>
      </div>

      <!-- HTTP 409 protected-package conflicts returned by the server.
           Each entry is { packageId, requiredOverrideLevel, message }.
           Rendered in red so the operator can see which package needs
           which override level, then click Back, re-approve the
           override, and resubmit. See
           shared/ChangePackages.md "Operator overrides". -->
      <div *ngIf="conflictWarnings.length > 0" class="cp-warning-bar cp-conflict-list">
        <mat-icon>block</mat-icon>
        <div>
          <div class="cp-conflict-title" i18n="@@Conflict details">Conflict details:</div>
          <ul>
            <li *ngFor="let c of conflictWarnings">{{getConflictText(c)}}</li>
          </ul>
        </div>
      </div>

      <!-- Post-save summary: server-returned warnings[] and
           uspsRefunds[]. Surfaced per shared/ChangePackages.md so the
           operator can verify the refund pipeline before closing. -->
      <div *ngIf="savedSummaryVisible" class="cp-save-summary">
        <div *ngIf="saveWarnings.length > 0" class="cp-warning-bar cp-warnings-list">
          <mat-icon>warning_amber</mat-icon>
          <div>
            <div class="cp-warnings-title" i18n="@@Save warnings">Save completed with warnings:</div>
            <ul>
              <li *ngFor="let w of saveWarnings">{{w}}</li>
            </ul>
          </div>
        </div>

        <div *ngIf="saveRefundResults.length > 0" class="cp-refunds-table">
          <div class="cp-refunds-title" i18n="@@USPS refund results">USPS refund results</div>
          <table>
            <thead>
              <tr>
                <th i18n="@@Package">Package</th>
                <th i18n="@@Tracking">Tracking</th>
                <th i18n="@@Provider">Provider</th>
                <th i18n="@@Status">Status</th>
                <th i18n="@@Reference">Reference</th>
                <th i18n="@@Support emailed">Support emailed</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let r of saveRefundResults" [class]="getRefundStatusClass(r.requestStatus)">
                <td>#{{r.packageId}}</td>
                <td>{{r.trackingNumber}}</td>
                <td>{{r.provider || '—'}}</td>
                <td>{{getRefundStatusLabel(r.requestStatus)}}</td>
                <td>
                  <span *ngIf="r.refundReferenceId">{{r.refundReferenceId}}</span>
                  <span *ngIf="!r.refundReferenceId && r.refundRequestId">#{{r.refundRequestId}}</span>
                </td>
                <td>
                  <mat-icon *ngIf="r.supportNotified" style="color: #2e7d32;">mark_email_read</mat-icon>
                  <mat-icon *ngIf="!r.supportNotified" style="color: #b71c1c;">unsubscribe</mat-icon>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="cp-footer">
        <button *ngIf="!savedSummaryVisible" mat-stroked-button (click)="goBackToEdit()" i18n="@@Back">Back</button>
        <button *ngIf="!savedSummaryVisible" mat-raised-button color="primary" (click)="saveChanges()"
                [disabled]="saving || hasQuantityMismatch" i18n="@@ConfirmSave">
          <mat-spinner *ngIf="saving" diameter="18" class="cp-inline-spinner"></mat-spinner>
          Confirm & Save
        </button>
        <button *ngIf="savedSummaryVisible" mat-raised-button color="primary"
                (click)="closeAfterSummary()" i18n="@@Close">Close</button>
      </div>
    </div>
  </ng-container>
</div>
